import { useEffect, useState } from 'react';

import { Box, HStack, Spinner, Text, Tooltip } from '@bonusly/design-system';

import { ActionCableProvider } from '../providers/action-cable-provider';

interface ConnectionStatusProps {
	provider: ActionCableProvider;
	showLabel?: boolean;
}

type ConnectionState = 'connected' | 'disconnected' | 'reconnecting' | 'syncing';

export function ConnectionStatus({ provider, showLabel = false }: ConnectionStatusProps) {
	const [state, setState] = useState<ConnectionState>(() => {
		if (provider.isReconnecting) return 'reconnecting';
		if (!provider.isConnected) return 'disconnected';
		if (!provider.isSynced) return 'syncing';
		return 'connected';
	});

	useEffect(() => {
		const updateState = () => {
			if (provider.isReconnecting) {
				setState('reconnecting');
			} else if (!provider.isConnected) {
				setState('disconnected');
			} else if (!provider.isSynced) {
				setState('syncing');
			} else {
				setState('connected');
			}
		};

		const handleStatus = () => updateState();
		const handleSynced = () => updateState();

		provider.on('status', handleStatus);
		provider.on('synced', handleSynced);
		provider.on('connection-error', handleStatus);

		return () => {
			provider.off('status', handleStatus);
			provider.off('synced', handleSynced);
			provider.off('connection-error', handleStatus);
		};
	}, [provider]);

	const config = {
		connected: {
			color: 'green.500',
			label: 'Connected',
			icon: '●',
		},
		syncing: {
			color: 'yellow.500',
			label: 'Syncing...',
			icon: <Spinner size="xs" color="yellow.500" />,
		},
		reconnecting: {
			color: 'yellow.500',
			label: 'Reconnecting...',
			icon: <Spinner size="xs" color="yellow.500" />,
		},
		disconnected: {
			color: 'red.500',
			label: 'Disconnected',
			icon: '●',
		},
	}[state];

	return (
		<Tooltip label={config.label} placement="bottom">
			<HStack spacing={2} align="center">
				<Box color={config.color} fontSize="lg" lineHeight="1">
					{config.icon}
				</Box>
				{showLabel && (
					<Text fontSize="sm" color="text.softer">
						{config.label}
					</Text>
				)}
			</HStack>
		</Tooltip>
	);
}
